import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  FormControl,
  FormLabel,
  Input,
  Select,
  Icon,
  Button,
  Badge,
  Flex,
  Divider,
  List,
  ListItem,
  ListIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import {
  FaSolarPanel,
  FaDollarSign,
  FaLeaf,
  FaChartLine,
  FaCheckCircle,
  FaBatteryFull,
  FaArrowRight,
  FaPhone,
  FaBolt,
  FaEnvelope,
  FaInfoCircle,
  FaCalculator,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

// Postcode prefix → sun hours & feed-in
function getLocationFromPostcode(postcode: string): { label: string; sunHours: number; feedIn: number } {
  const prefix = postcode.trim().slice(0, 2);
  if (prefix === "08") return { label: "NT", sunHours: 6.0, feedIn: 0.12 };
  const first = postcode.trim()[0];
  switch (first) {
    case "0": return { label: "ACT", sunHours: 4.8, feedIn: 0.09 };
    case "2": return { label: "NSW", sunHours: 4.5, feedIn: 0.10 };
    case "3": return { label: "VIC", sunHours: 4.2, feedIn: 0.09 };
    case "4": return { label: "QLD", sunHours: 5.2, feedIn: 0.11 };
    case "5": return { label: "SA",  sunHours: 5.0, feedIn: 0.10 };
    case "6": return { label: "WA",  sunHours: 5.8, feedIn: 0.10 };
    case "7": return { label: "TAS", sunHours: 4.0, feedIn: 0.09 };
    default:  return { label: "QLD", sunHours: 5.2, feedIn: 0.11 };
  }
}

function calculate(
  billAmount: number,
  billingPeriod: "monthly" | "quarterly" | "yearly",
  postcode: string,
  daytimePct: number,
  offsetPct: number
) {
  const multiplier = billingPeriod === "monthly" ? 12 : billingPeriod === "quarterly" ? 4 : 1;
  const annualBill = billAmount * multiplier;
  const avgRate = 0.32; // $/kWh average Australian rate
  const annualKWh = annualBill / avgRate;

  const loc = getLocationFromPostcode(postcode);
  const { sunHours, feedIn } = loc;

  // System size to achieve target offset
  const targetGeneration = annualKWh * (offsetPct / 100);
  const rawSystem = targetGeneration / (sunHours * 365 * 0.85);
  const suggestedKW = Math.max(3, Math.ceil(rawSystem * 2) / 2); // min 3kW, round to 0.5

  // What the system actually generates
  const annualGeneration = suggestedKW * sunHours * 365 * 0.85;

  // Split generation into self-consumed vs exported
  const daytimeFraction = daytimePct / 100;
  const selfConsumed = Math.min(annualGeneration * daytimeFraction, annualKWh * daytimeFraction);
  const exported = Math.max(0, annualGeneration - selfConsumed);

  const annualSavings = selfConsumed * avgRate + exported * feedIn;
  const afterSolarAnnual = Math.max(0, annualBill - annualSavings);

  // Convert after-solar back to user's billing period
  const afterSolarPeriod = afterSolarAnnual / multiplier;

  // 25-year savings with 3% annual electricity price escalation
  const savings25yr = annualSavings * ((Math.pow(1.03, 25) - 1) / 0.03);

  // Battery: useful if daytime use < 50% (lots of evening load)
  const batteryRecommended = daytimePct < 50;
  const batteryInsight = batteryRecommended
    ? "Battery recommended — your evening usage is high"
    : "Battery optional — most savings from daytime self-use";

  // CO2 offset (0.82 kg CO2 per kWh, Australian grid average)
  const co2Tons = (annualGeneration * 0.82) / 1000;

  return {
    suggestedKW,
    afterSolarPeriod,
    annualSavings,
    savings25yr,
    batteryInsight,
    batteryRecommended,
    co2Tons,
    loc,
    annualBill,
    annualGeneration,
  };
}

const ResultCard: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
  children?: React.ReactNode;
}> = ({ icon, label, value, sub, highlight, children }) => (
  <Box
    p={7}
    bg={highlight ? NAVY : "white"}
    borderRadius="2xl"
    boxShadow="lg"
    border="2px solid"
    borderColor={highlight ? NAVY : `${SOLAR_GOLD}50`}
    _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
    transition="all 0.2s"
    display="flex"
    flexDirection="column"
  >
    <Stat flex="1">
      <HStack mb={3} spacing={2}>
        <Icon as={icon} boxSize={6} color={SOLAR_GOLD} />
        <StatLabel fontSize="sm" fontWeight="700" color={highlight ? "gray.300" : "gray.600"} textTransform="uppercase" letterSpacing="wider">
          {label}
        </StatLabel>
      </HStack>
      <StatNumber fontSize="3xl" fontWeight="900" color={highlight ? "white" : NAVY} lineHeight="1.1">
        {value}
      </StatNumber>
      <StatHelpText fontSize="xs" color={highlight ? "gray.400" : "gray.500"} mt={1}>
        {sub}
      </StatHelpText>
    </Stat>
    {children}
  </Box>
);

const SolarCalculator: React.FC = () => {
  const [billAmount, setBillAmount] = useState<string>("350");
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "quarterly" | "yearly">("quarterly");
  const [postcode, setPostcode] = useState("4000");
  const [daytimePct, setDaytimePct] = useState(55);
  const [offsetPct, setOffsetPct] = useState(80);
  const [showResults, setShowResults] = useState(false);

  // Email results form
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailName, setEmailName] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [emailPhone, setEmailPhone] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const bill = parseFloat(billAmount) || 0;
  const results = calculate(bill, billingPeriod, postcode, daytimePct, offsetPct);

  const periodLabel = billingPeriod === "monthly" ? "/month" : billingPeriod === "quarterly" ? "/quarter" : "/year";

  const handleShowSavings = useCallback(() => {
    setShowResults(true);
    setTimeout(() => {
      document.getElementById("calc-results")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const handleEmailSubmit = () => {
    // In production this would POST to an API
    setEmailSent(true);
  };

  const postcodeValid = /^\d{4}$/.test(postcode.trim());

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/calculator"
        fallbackTitle="Solar Savings Calculator | One Solar Australia"
        fallbackDescription="Estimate your potential solar savings, recommended system size, environmental impact and battery suitability — built for Australian homes."
      />
      <NavbarWithCallToAction />

      {/* ── Hero ── */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaCalculator} boxSize={16} color={SOLAR_GOLD} />
            <Heading size="2xl" fontWeight="bold">
              Solar Savings Calculator
            </Heading>
            <Text fontSize="xl" color="rgba(255,255,255,0.85)" maxW="2xl">
              Estimate your potential solar savings, recommended system size, environmental
              impact and battery suitability — built for Australian homes.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* ── Calculator Inputs ── */}
      <Box bg="gray.50" py={{ base: 14, md: 20 }}>
        <Container maxW="container.md">
          <VStack spacing={6} mb={10} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Your Details
            </Badge>
            <Heading size="xl" color={NAVY}>Enter Your Electricity Details</Heading>
            <Text color="gray.600" fontSize="md">
              We use your bill, postcode, and usage patterns to tailor your estimate.
            </Text>
          </VStack>

          <Box
            bg="white"
            borderRadius="2xl"
            p={{ base: 6, md: 10 }}
            boxShadow="xl"
            border="2px solid"
            borderColor={`${SOLAR_GOLD}30`}
          >
            <VStack spacing={8} align="stretch">

              {/* Bill + Period */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired>
                  <FormLabel fontWeight="700" color={NAVY} fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                    Average Electricity Bill
                  </FormLabel>
                  <Box position="relative">
                    <Box
                      position="absolute"
                      left={4}
                      top="50%"
                      transform="translateY(-50%)"
                      color={SOLAR_GOLD}
                      fontWeight="800"
                      fontSize="lg"
                      zIndex={1}
                    >
                      $
                    </Box>
                    <Input
                      type="number"
                      value={billAmount}
                      onChange={(e) => setBillAmount(e.target.value)}
                      size="lg"
                      pl={8}
                      bg="gray.50"
                      borderColor="gray.200"
                      _focus={{ borderColor: SOLAR_GOLD, boxShadow: `0 0 0 1px ${SOLAR_GOLD}` }}
                      fontSize="xl"
                      fontWeight="700"
                    />
                  </Box>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="700" color={NAVY} fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                    Billing Period
                  </FormLabel>
                  <Select
                    value={billingPeriod}
                    onChange={(e) => setBillingPeriod(e.target.value as "monthly" | "quarterly" | "yearly")}
                    size="lg"
                    bg="gray.50"
                    borderColor="gray.200"
                    _focus={{ borderColor: SOLAR_GOLD, boxShadow: `0 0 0 1px ${SOLAR_GOLD}` }}
                    fontWeight="600"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              {/* Postcode */}
              <FormControl isRequired>
                <FormLabel fontWeight="700" color={NAVY} fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                  Postcode
                </FormLabel>
                <Input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="e.g. 4000"
                  maxLength={4}
                  size="lg"
                  bg="gray.50"
                  borderColor="gray.200"
                  _focus={{ borderColor: SOLAR_GOLD, boxShadow: `0 0 0 1px ${SOLAR_GOLD}` }}
                />
                {postcodeValid && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Detected: {results.loc.label} — {results.loc.sunHours} peak sun hours/day
                  </Text>
                )}
              </FormControl>

              <Divider />

              {/* Daytime use slider */}
              <FormControl>
                <Flex justify="space-between" align="baseline" mb={3}>
                  <FormLabel fontWeight="700" color={NAVY} fontSize="sm" textTransform="uppercase" letterSpacing="wider" mb={0}>
                    Daytime Energy Use
                  </FormLabel>
                  <Text fontWeight="900" fontSize="2xl" color={SOLAR_GOLD}>{daytimePct}%</Text>
                </Flex>
                <Slider
                  value={daytimePct}
                  onChange={setDaytimePct}
                  min={10}
                  max={90}
                  step={5}
                  colorScheme="yellow"
                >
                  <SliderTrack bg="gray.200" h={2} borderRadius="full">
                    <SliderFilledTrack bg={SOLAR_GOLD} />
                  </SliderTrack>
                  <SliderThumb boxSize={7} bg={SOLAR_GOLD} border="2px solid white" boxShadow="md" />
                </Slider>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="xs" color="gray.400">Mostly at night</Text>
                  <Text fontSize="xs" color="gray.400">Mostly during day</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  The percentage of your electricity you use while solar panels are generating (roughly 7am–5pm).
                </Text>
              </FormControl>

              {/* Bill offset slider */}
              <FormControl>
                <Flex justify="space-between" align="baseline" mb={3}>
                  <FormLabel fontWeight="700" color={NAVY} fontSize="sm" textTransform="uppercase" letterSpacing="wider" mb={0}>
                    Bill Offset Target
                  </FormLabel>
                  <Text fontWeight="900" fontSize="2xl" color={SOLAR_GOLD}>{offsetPct}%</Text>
                </Flex>
                <Slider
                  value={offsetPct}
                  onChange={setOffsetPct}
                  min={20}
                  max={100}
                  step={5}
                  colorScheme="yellow"
                >
                  <SliderTrack bg="gray.200" h={2} borderRadius="full">
                    <SliderFilledTrack bg={SOLAR_GOLD} />
                  </SliderTrack>
                  <SliderThumb boxSize={7} bg={SOLAR_GOLD} border="2px solid white" boxShadow="md" />
                </Slider>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="xs" color="gray.400">Partial offset</Text>
                  <Text fontSize="xs" color="gray.400">Full offset</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  How much of your electricity bill you'd like solar to cover.
                </Text>
              </FormControl>

              <Button
                bg={SOLAR_GOLD}
                color="white"
                size="lg"
                onClick={handleShowSavings}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                transition="all 0.2s"
                h={14}
                fontSize="md"
                fontWeight="700"
                isDisabled={!bill || !postcodeValid}
              >
                Show My Estimated Savings
              </Button>

              {(!bill || !postcodeValid) && (
                <Text fontSize="xs" color="gray.400" textAlign="center" mt={-4}>
                  Enter a bill amount and valid 4-digit postcode to calculate.
                </Text>
              )}
            </VStack>
          </Box>
        </Container>
      </Box>

      {/* ── Results ── */}
      {showResults && (
        <Box id="calc-results" py={{ base: 16, md: 24 }} bg="white">
          <Container maxW="container.xl">
            <VStack spacing={4} mb={12} textAlign="center">
              <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                Your Estimate
              </Badge>
              <Heading size="xl" color={NAVY}>
                Your Solar Savings Estimate
              </Heading>
              <Text color="gray.600" fontSize="md" maxW="xl">
                Based on a ${bill.toLocaleString()} {billingPeriod} bill in {results.loc.label} with {daytimePct}% daytime use and a {offsetPct}% offset target.
              </Text>
            </VStack>

            {/* 6 Result Cards */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6} mb={12}>
              <ResultCard
                icon={FaSolarPanel}
                label="Suggested System"
                value={`${results.suggestedKW} kW`}
                sub={`To offset ${offsetPct}% of your ${billingPeriod} bill`}
                highlight
              />
              <ResultCard
                icon={FaDollarSign}
                label="After Solar"
                value={`$${Math.round(results.afterSolarPeriod).toLocaleString()}${periodLabel}`}
                sub={`Down from $${bill.toLocaleString()}${periodLabel}`}
              />
              <ResultCard
                icon={FaChartLine}
                label="Annual Savings"
                value={`$${Math.round(results.annualSavings).toLocaleString()}`}
                sub={`~$${Math.round(results.annualSavings / 12).toLocaleString()} per month`}
              />
              <ResultCard
                icon={FaDollarSign}
                label="25-Year Savings"
                value={`$${Math.round(results.savings25yr).toLocaleString()}`}
                sub="Assuming 3% annual electricity price increase"
              />
              <ResultCard
                icon={results.batteryRecommended ? FaBatteryFull : FaBolt}
                label="Battery Insight"
                value={results.batteryRecommended ? "Recommended" : "Optional"}
                sub={results.batteryInsight}
              >
                <Button
                  as={RouterLink}
                  to="/calculators/solar-battery-roi"
                  size="sm"
                  mt={5}
                  bg={SOLAR_GOLD}
                  color="white"
                  _hover={{ bg: SOLAR_GOLD_HOVER }}
                  rightIcon={<FaArrowRight />}
                  w="full"
                  fontWeight="700"
                >
                  Check Battery ROI
                </Button>
              </ResultCard>
              <ResultCard
                icon={FaLeaf}
                label="Environmental Impact"
                value={`${results.co2Tons.toFixed(1)} tonnes`}
                sub="CO₂ offset per year"
              />
            </SimpleGrid>

            {/* Notes */}
            <Box
              p={6}
              bg="gray.50"
              borderRadius="2xl"
              border="1px solid"
              borderColor={`${SOLAR_GOLD}40`}
              mb={12}
            >
              <HStack spacing={3} mb={4}>
                <Icon as={FaInfoCircle} color={SOLAR_GOLD} boxSize={5} flexShrink={0} />
                <Heading size="sm" color={NAVY}>How These Estimates Work</Heading>
              </HStack>
              <List spacing={2}>
                {[
                  "Estimates use your electricity bill, postcode sun-hours, daytime usage and offset target to derive a recommended system size.",
                  "Actual outcomes vary with roof orientation, shading, switchboard setup and available roof space.",
                  "Higher evening usage (low daytime %) may justify battery storage — a specialist can confirm.",
                  "25-year savings apply a 3% annual electricity price escalation and 0.5% annual panel degradation.",
                  "A renewable energy specialist from One Solar Australia may contact you to discuss your results.",
                ].map((note) => (
                  <ListItem key={note} display="flex" alignItems="flex-start" gap={3}>
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} mt="3px" flexShrink={0} boxSize={3} />
                    <Text fontSize="sm" color="gray.600" lineHeight="1.7">{note}</Text>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Email Results */}
            <Box
              bg={NAVY}
              borderRadius="2xl"
              p={{ base: 8, md: 12 }}
              mb={0}
            >
              {emailSent ? (
                <VStack spacing={4} textAlign="center" color="white">
                  <Icon as={FaCheckCircle} boxSize={14} color={SOLAR_GOLD} />
                  <Heading size="lg">Results Sent!</Heading>
                  <Text color="gray.300">
                    We've emailed your estimates. A solar specialist may be in touch to discuss your options.
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/solar/quote"
                    bg={SOLAR_GOLD}
                    color="white"
                    size="lg"
                    _hover={{ bg: SOLAR_GOLD_HOVER }}
                    rightIcon={<FaArrowRight />}
                  >
                    Get a Detailed Free Quote
                  </Button>
                </VStack>
              ) : !showEmailForm ? (
                <VStack spacing={6} textAlign="center" color="white">
                  <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                    Save Your Results
                  </Badge>
                  <Heading size="xl">Email My Results</Heading>
                  <Text fontSize="lg" color="gray.300" maxW="xl" lineHeight="1.8">
                    Get your personalised savings estimate emailed to you — plus a free, no-obligation
                    consultation with one of our solar specialists.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap" justify="center">
                    <Button
                      bg={SOLAR_GOLD}
                      color="white"
                      size="lg"
                      leftIcon={<FaEnvelope />}
                      _hover={{ bg: SOLAR_GOLD_HOVER }}
                      onClick={() => setShowEmailForm(true)}
                      px={8}
                    >
                      Email My Results
                    </Button>
                    <Button
                      as={RouterLink}
                      to="/solar/quote"
                      size="lg"
                      variant="outline"
                      borderColor="white"
                      color="white"
                      _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                      px={8}
                    >
                      Get Free Quote
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <VStack spacing={6} align="stretch" maxW="600px" mx="auto">
                  <VStack spacing={2} textAlign="center" color="white">
                    <Heading size="lg">Email My Results</Heading>
                    <Text color="gray.300" fontSize="sm">
                      A renewable energy specialist may contact you to discuss your options.
                    </Text>
                  </VStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel color="gray.300" fontSize="sm">Full Name</FormLabel>
                      <Input
                        value={emailName}
                        onChange={(e) => setEmailName(e.target.value)}
                        placeholder="Jane Smith"
                        size="lg"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        color="white"
                        _placeholder={{ color: "gray.500" }}
                        _focus={{ borderColor: SOLAR_GOLD }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel color="gray.300" fontSize="sm">Email Address</FormLabel>
                      <Input
                        type="email"
                        value={emailAddr}
                        onChange={(e) => setEmailAddr(e.target.value)}
                        placeholder="jane@example.com"
                        size="lg"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        color="white"
                        _placeholder={{ color: "gray.500" }}
                        _focus={{ borderColor: SOLAR_GOLD }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel color="gray.300" fontSize="sm">Phone Number</FormLabel>
                    <Input
                      type="tel"
                      value={emailPhone}
                      onChange={(e) => setEmailPhone(e.target.value)}
                      placeholder="0400 000 000"
                      size="lg"
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      color="white"
                      _placeholder={{ color: "gray.500" }}
                      _focus={{ borderColor: SOLAR_GOLD }}
                    />
                  </FormControl>
                  <Button
                    bg={SOLAR_GOLD}
                    color="white"
                    size="lg"
                    _hover={{ bg: SOLAR_GOLD_HOVER }}
                    onClick={handleEmailSubmit}
                    isDisabled={!emailName || !emailAddr}
                    rightIcon={<FaArrowRight />}
                  >
                    Send My Estimate
                  </Button>
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Electrical Contractors Licence No. 88686 — One Group Australasia Pty Ltd ACN 642 690 435
                  </Text>
                </VStack>
              )}
            </Box>
          </Container>
        </Box>
      )}

      {/* ── How It Works ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              How It Works
            </Badge>
            <Heading size="xl" color={NAVY}>What the Calculator Uses</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Your results are derived from four inputs, cross-referenced with Australian solar data.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {[
              {
                step: "01",
                icon: FaDollarSign,
                title: "Your Electricity Bill",
                body: "We convert your bill amount and billing period into an annual electricity spend to size your system correctly.",
              },
              {
                step: "02",
                icon: FaSolarPanel,
                title: "Postcode & Sun Hours",
                body: "Your postcode maps to a state and average peak sun hours per day, which determines how much power your panels generate.",
              },
              {
                step: "03",
                icon: FaBolt,
                title: "Daytime Energy Use",
                body: "The more you use electricity during the day (when panels generate), the higher your self-consumption and savings.",
              },
              {
                step: "04",
                icon: FaChartLine,
                title: "Bill Offset Target",
                body: "Your target offset determines the recommended system size — a higher target means more panels and larger savings.",
              },
            ].map((item) => (
              <Box
                key={item.step}
                bg="white"
                borderRadius="2xl"
                p={7}
                boxShadow="md"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
                transition="all 0.3s"
              >
                <Box
                  w={10} h={10}
                  bg={SOLAR_GOLD}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="black"
                  fontSize="sm"
                  color="white"
                  mb={4}
                  boxShadow="0 2px 8px rgba(198,146,20,0.4)"
                >
                  {item.step}
                </Box>
                <Icon as={item.icon} boxSize={7} color={SOLAR_GOLD} mb={3} />
                <Heading size="sm" color={NAVY} mb={2}>{item.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.8">{item.body}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Bottom CTA ── */}
      <Box
        position="relative"
        py={{ base: 20, md: 24 }}
        backgroundImage="url('/assets/solar-assets/asset%2020.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: `rgba(20, 28, 73, 0.88)`,
          zIndex: 0,
        }}
      >
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="2xl" fontWeight="black">
              Ready for a Detailed Quote?
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="xl" lineHeight="1.8">
              Calculator estimates are a starting point. Our CEC-accredited specialists provide a
              free, no-obligation assessment based on your actual roof and usage.
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={10}
              >
                Get Free Quote
              </Button>
              <Button
                as="a"
                href="tel:1300663765"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                leftIcon={<FaPhone />}
                px={8}
              >
                1300 663 765
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarCalculator;
